import { EffectsCommandMap } from 'dva';
import { FServiceAPI, FI18n } from '@freelog/tools-lib';
import { ConnectState } from '@/models/connect';
import type {
  ChangeAction,
  Info_OnMount_Page_Action,
  Info_OnUnmount_Page_Action,
  Info_OnClick_EditTitleBtn_Action,
  Info_OnClick_CancelEditTitleBtn_Action,
  Info_OnClick_SaveTitleBtn_Action,
  Info_OnChange_TitleInput_Action,
  Info_OnClick_SaveIntroductionBtn_Action,
  Info_OnChange_Cover_Action,
  Info_OnChange_Labels_Action,
  Info_Post_UpdateSetting_Action,
  Sider_OnUpdate_Data_Action,
} from './types';
import { initialState } from './initialState';
import { isRssRelatedResource } from '@/utils/rss';

export const infoEffects = {
  *info_onMount_Page({ payload }: Info_OnMount_Page_Action, { put, call }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        info_resourceID: payload.resourceID,
        info_pageState: 'loading',
      },
    });

    const params: Parameters<typeof FServiceAPI.Resource.info>[0] = {
      resourceIdOrName: payload.resourceID,
      isLoadPolicyInfo: 1,
      isTranslate: 1,
    };
    const {
      data: data_resourceInfo,
    }: {
      data: {
        coverImages: string[];
        resourceName: string;
        resourceTitle: string;
        resourceTypeCode: string;
        resourceType: string[];
        intro: string;
        tags: string[];
        serializeStatus: 0 | 1;
        username: string;
        feedUrl?: string;
        rssGuid?: string;
        rssPubDate?: string;
      };
    } = yield call(FServiceAPI.Resource.info, params);

    const isRss = isRssRelatedResource(data_resourceInfo);
    const feedUrl = (data_resourceInfo.feedUrl || '').trim();

    const params6: Parameters<typeof FServiceAPI.Resource.getCollectionCollectRules>[0] = {
      resourceId: payload.resourceID,
    };

    const {
      data: data_CollectionCollectRules,
    }: {
      data: null | {
        status: 0 | 1;
        conditionType: 1 | 2;
        filterConditions: {
          key: 'resourceTitle' | 'resourceTypeCode' | 'authIdentity';
          limitOperatorType:
            | 'INCLUDES'
            | 'NOT_INCLUDES'
            | 'STARTS_WITH'
            | 'ENDS_WITH'
            | 'EQUAL'
            | 'NOT_EQUAL';
          value: string;
        }[];
      };
    } = yield call(FServiceAPI.Resource.getCollectionCollectRules, params6);

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        info_resourceInfo: {
          coverImages: data_resourceInfo.coverImages,
          resourceName: data_resourceInfo.resourceName,
          resourceTypeCode: data_resourceInfo.resourceTypeCode,
          resourceType: data_resourceInfo.resourceType,
          resourceTitle: data_resourceInfo.resourceTitle,
          intro: data_resourceInfo.intro,
          tags: data_resourceInfo.tags,
          // 仅 feedUrl 等 RSS 字段非空时视为 RSS 合集；未绑定前不锁定编辑、不展示 RSS 地址模块
          rssSource: isRss ? 'yes' : 'no',
          rssUrl: feedUrl,
        },
        info_pageState: 'loaded',
        info_introduction_EditorText: data_resourceInfo.intro,

        info_username: data_resourceInfo.username,
        info_updateSetting: {
          isFinish: data_resourceInfo.serializeStatus === 1,
          automatic: data_CollectionCollectRules?.status === 1 || false,
          conditionType: data_CollectionCollectRules?.conditionType === 1 ? 'every' : 'some',
          conditions:
            data_CollectionCollectRules?.filterConditions.map((fc) => {
              return {
                ...fc,
                value: fc.value.includes('/') ? fc.value.split('/')[1] : fc.value,
                valueError: '',
              };
            }) || [],
        },
      },
    });
  },
  *info_onUnmount_Page({}: Info_OnUnmount_Page_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: initialState,
    });
  },

  *info_onClick_EditTitleBtn(
    {}: Info_OnClick_EditTitleBtn_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { collectionManager } = yield select(({ collectionManager }: ConnectState) => ({
      collectionManager,
    }));
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        info_title_IsEditing: true,
        info_title_Input: collectionManager.info_resourceInfo?.resourceTitle || '',
        info_title_Error: '',
      },
    });
  },
  *info_onClick_CancelEditTitleBtn(
    {}: Info_OnClick_CancelEditTitleBtn_Action,
    { put }: EffectsCommandMap,
  ) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        info_title_IsEditing: false,
      },
    });
  },
  *info_onClick_SaveEditTitleBtn(
    { payload }: Info_OnClick_SaveTitleBtn_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { collectionManager } = yield select(({ collectionManager }: ConnectState) => ({
      collectionManager,
    }));

    const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
      resourceId: collectionManager.info_resourceID,
      resourceTitle: collectionManager.info_title_Input,
    };
    yield call(FServiceAPI.Resource.update, params);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        info_resourceInfo: collectionManager.info_resourceInfo
          ? {
              ...collectionManager.info_resourceInfo,
              resourceTitle: collectionManager.info_title_Input,
            }
          : null,
        info_title_IsEditing: false,
      },
    });
    yield put<Sider_OnUpdate_Data_Action>({
      type: 'collectionManager/sider_onUpdate_Data',
    });
  },
  *info_onChange_TitleInput(
    { payload }: Info_OnChange_TitleInput_Action,
    { put }: EffectsCommandMap,
  ) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        info_title_Input: payload.value,
        info_title_Error:
          payload.value === ''
            ? FI18n.i18nNext.t('naming_convention_resource_title_required')
            : payload.value.length > 100
            ? '不超过100个字符'
            : '',
      },
    });
  },
  *info_onClick_SaveIntroductionBtn(
    {}: Info_OnClick_SaveIntroductionBtn_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { collectionManager } = yield select(({ collectionManager }: ConnectState) => ({
      collectionManager,
    }));

    const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
      resourceId: collectionManager.info_resourceID,
      intro: collectionManager.info_introduction_EditorText,
    };
    yield call(FServiceAPI.Resource.update, params);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        info_resourceInfo: collectionManager.info_resourceInfo
          ? {
              ...collectionManager.info_resourceInfo,
              intro: collectionManager.info_introduction_EditorText.trim(),
            }
          : null,
      },
    });
  },
  *info_onChange_Cover(
    { payload }: Info_OnChange_Cover_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { collectionManager } = yield select(({ collectionManager }: ConnectState) => ({
      collectionManager,
    }));

    const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
      resourceId: collectionManager.info_resourceID,
      coverImages: [payload.value],
    };
    yield call(FServiceAPI.Resource.update, params);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        info_resourceInfo: collectionManager.info_resourceInfo
          ? {
              ...collectionManager.info_resourceInfo,
              coverImages: [payload.value],
            }
          : null,
      },
    });
    yield put<Sider_OnUpdate_Data_Action>({
      type: 'collectionManager/sider_onUpdate_Data',
    });
  },
  *info_onChange_Labels(
    { payload }: Info_OnChange_Labels_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { collectionManager } = yield select(({ collectionManager }: ConnectState) => ({
      collectionManager,
    }));

    const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
      resourceId: collectionManager.info_resourceID,
      tags: payload.value,
    };
    yield call(FServiceAPI.Resource.update, params);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        info_resourceInfo: collectionManager.info_resourceInfo
          ? {
              ...collectionManager.info_resourceInfo,
              tags: payload.value,
            }
          : null,
      },
    });
  },
  *info_Post_UpdateSetting(
    {}: Info_Post_UpdateSetting_Action,
    { put, call, select }: EffectsCommandMap,
  ) {
    const { collectionManager }: ConnectState = yield select(
      ({ collectionManager }: ConnectState) => ({
        collectionManager,
      }),
    );

    const params: Parameters<typeof FServiceAPI.Resource.setCollectRules>[0] = {
      resourceId: collectionManager.info_resourceID,
      // @ts-ignore
      serializeStatus: collectionManager.info_updateSetting.isFinish ? 1 : 0,
      status: collectionManager.info_updateSetting.automatic ? 1 : 0,
      conditionType: collectionManager.info_updateSetting.conditionType === 'every' ? 1 : 2,
      filterConditions: collectionManager.info_updateSetting.conditions.map((c) => {
        return {
          key: c.key,
          limitOperatorType: c.limitOperatorType,
          value: c.value,
        };
      }),
    };

    yield call(FServiceAPI.Resource.setCollectRules, params);
  },
};
